/**
 * Data layer for `agency-features`. Thin typed fetch wrappers over
 * `GET /api/v1/agency/features`. Raw `fetch()` is confined to this file
 * per the dash contract (D6).
 *
 * The backend returns the full plan/feature matrix for the caller's current
 * plan. The UI renders a cross-plan comparison table (Starter / Agency /
 * Enterprise) with the current plan highlighted and an Upgrade CTA.
 */

export const AGENCY_FEATURE_KEYS = [
	"multi_client",
	"sub_admins",
	"white_label",
	"smartlink_manual",
	"campaign_grouping",
] as const;

export type AgencyFeatureKey = (typeof AGENCY_FEATURE_KEYS)[number];

export const PLAN_IDS = ["starter", "agency", "enterprise"] as const;
export type PlanId = (typeof PLAN_IDS)[number];

export interface AgencyFeaturesResponse {
	plan: string | null;
	features: Record<AgencyFeatureKey, boolean>;
	enabled: AgencyFeatureKey[];
}

/**
 * Static presentation metadata for each feature row. Kept on the UI side
 * because the API deliberately returns only booleans — copy is marketing
 * surface, not a data concern.
 */
export const AGENCY_FEATURE_META: Record<
	AgencyFeatureKey,
	{ label: string; description: string }
> = {
	multi_client: {
		label: "Multi-client management",
		description:
			"Manage many child tenants / clients under one agency login.",
	},
	sub_admins: {
		label: "Scoped sub-admins",
		description:
			"Invite scoped sub-admin users per client or workspace.",
	},
	white_label: {
		label: "White-label portals",
		description:
			"Fully rebrand portals — logo, colours, favicon, custom domain.",
	},
	smartlink_manual: {
		label: "Manual SmartLink builder",
		description:
			"Hand-curated SmartLink rule builder with geo / device / OS routing.",
	},
	campaign_grouping: {
		label: "Campaign grouping",
		description:
			"Organise campaigns into named buckets with bulk operations.",
	},
};

/**
 * Canonical cross-plan matrix used to render the comparison table.
 * Mirrors apps/api/src/lib/agency-gate.ts → AGENCY_FEATURE_MATRIX.
 * Enterprise inherits every Agency capability.
 */
export const PLAN_FEATURE_MATRIX: Record<
	PlanId,
	Record<AgencyFeatureKey, boolean>
> = {
	starter: {
		multi_client: false,
		sub_admins: false,
		white_label: false,
		smartlink_manual: false,
		campaign_grouping: false,
	},
	agency: {
		multi_client: true,
		sub_admins: true,
		white_label: true,
		smartlink_manual: true,
		campaign_grouping: true,
	},
	enterprise: {
		multi_client: true,
		sub_admins: true,
		white_label: true,
		smartlink_manual: true,
		campaign_grouping: true,
	},
};

export const PLAN_LABELS: Record<PlanId, string> = {
	starter: "Starter",
	agency: "Agency",
	enterprise: "Enterprise",
};

const BASE = "/api/v1/agency/features";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

/**
 * Fetch the feature matrix for the caller's active plan.
 */
export async function getAgencyFeatures(): Promise<AgencyFeaturesResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<AgencyFeaturesResponse>(res);
}

/**
 * Narrow the free-form `plan` string (may be null or any server value) into
 * a canonical PlanId when possible, else null.
 */
export function toPlanId(plan: string | null | undefined): PlanId | null {
	if (!plan) return null;
	const normalised = plan.toLowerCase().trim();
	if ((PLAN_IDS as readonly string[]).includes(normalised)) {
		return normalised as PlanId;
	}
	return null;
}

export const agencyFeaturesQueryKeys = {
	all: ["agency-features"] as const,
	list: () => [...agencyFeaturesQueryKeys.all] as const,
	detail: (id: string) => [...agencyFeaturesQueryKeys.all, id] as const,
};
